import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const TikTokShowcase: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    loop: true,
    dragFree: false,
    containScroll: "trimSnaps",
  });

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    
    // TikTok Videos müssen nach jedem DOM-Wechsel neu initialisiert werden.
    // Wir erzwingen das Laden des Skripts, falls es noch nicht da ist,
    // oder rufen die globale reload-Funktion auf.
    setTimeout(() => {
      // @ts-ignore
      if (window.tiktok && typeof window.tiktok.reload === 'function') {
        // @ts-ignore
        window.tiktok.reload();
      } else {
        // Falls das Skript nicht reagiert, laden wir es kurz neu
        const existingScript = document.querySelector('script[src*="tiktok.com/embed.js"]');
        if (existingScript) {
          existingScript.remove();
          const script = document.createElement("script");
          script.src = "https://www.tiktok.com/embed.js";
          script.async = true;
          document.body.appendChild(script);
        }
      }
    }, 100);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", onSelect);
    emblaApi.on("pointerDown", onSelect);
  }, [emblaApi, onSelect]);

  useEffect(() => {
    const consent = localStorage.getItem("wildfoerster_consent") === "granted";
    setHasConsent(consent);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    
    if (consent) {
      // Standard TikTok Embed Script
      if (!document.querySelector('script[src="https://www.tiktok.com/embed.js"]')) {
        const script = document.createElement("script");
        script.src = "https://www.tiktok.com/embed.js";
        script.async = true;
        document.body.appendChild(script);
      }

      // Workaround for undefined 'prod' in TikTok SDK
      // @ts-ignore
      window.tiktok = window.tiktok || {};
      // @ts-ignore
      window.tiktok._config = window.tiktok._config || { env: 'prod' };

      return () => {
        observer.disconnect();
      };
    }
    
    return () => observer.disconnect();
  }, []);

  // Beispiel-IDs von TikTok-Videos
  const videoIds = [
    "7540035986089053462",
    "7531405880940596502",
    "7530732135058083094",
    "7530730611128945942",
    "7526263305162247446",
  ];

  return (
    <section 
      ref={sectionRef} 
      className="bg-black py-32 px-6 relative overflow-hidden"
    >
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row items-center justify-between mb-20 gap-8">
          <div className="text-center md:text-left">
            <h2 className="text-5xl md:text-7xl font-black uppercase tracking-tighter text-white">
              TikTok <span className="text-[#cfc297]">Moments</span>
            </h2>
            <p className="text-[#cfc297] text-xs uppercase tracking-[0.8em] font-black mt-4">@wildfoerster13</p>
          </div>

          <div className="flex gap-4">
            <button
              onClick={scrollPrev}
              className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center hover:bg-[#cfc297] hover:text-black transition-all duration-300 text-white"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={scrollNext}
              className="w-12 h-12 rounded-full border border-white/10 flex items-center justify-center hover:bg-[#cfc297] hover:text-black transition-all duration-300 text-white"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="embla overflow-hidden" ref={emblaRef}>
          <div className="embla__container flex">
            {!hasConsent ? (
              <div className="w-full flex flex-col items-center justify-center min-h-[400px] border border-white/5 bg-black rounded-2xl p-12 text-center">
                <p className="text-white/60 mb-6 uppercase tracking-widest text-xs">
                  Inhalt von TikTok blockiert. Bitte akzeptieren Sie die Cookies, um dieses Video zu sehen.
                </p>
                <button 
                  onClick={() => {
                    localStorage.setItem("wildfoerster_consent", "granted");
                    window.location.reload();
                  }}
                  className="px-8 py-3 bg-[#cfc297] text-black font-black uppercase text-[10px] tracking-widest rounded-full hover:bg-white transition-all"
                >
                  TikTok erlauben
                </button>
              </div>
            ) : (
              videoIds.map((id, idx) => {
                const isSelected = selectedIndex === idx;
                // Auf Mobile rendern wir alle Videos, da das Swipen sonst die Animation unterbricht
                // Auf Desktop rendern wir nur das aktive (Blur/Scale-Effekt)
                const shouldRenderEmbed = isMobile || isSelected;

                return (
                  <div
                    key={`${id}-${idx}`}
                    className="embla__slide flex-[0_0_85%] sm:flex-[0_0_50%] lg:flex-[0_0_33.333%] px-4 min-w-0"
                  >
                    <div
                      className={`transition-all duration-700 transform ${
                        isVisible
                          ? "opacity-100 translate-y-0"
                          : "opacity-0 translate-y-10"
                      } ${
                        !isMobile && !isSelected
                          ? "scale-[0.85] blur-md opacity-30 grayscale-[80%] pointer-events-none"
                          : "scale-100 blur-none opacity-100 pointer-events-auto"
                      }`}
                    >
                      {shouldRenderEmbed ? (
                        <blockquote
                          className="tiktok-embed rounded-2xl overflow-hidden border border-white/5 bg-black shadow-2xl"
                          cite={`https://www.tiktok.com/@wildfoerster13/video/${id}`}
                          data-video-id={id}
                          style={{ minWidth: "325px", maxWidth: "605px", height: "575px", background: 'black' }}
                        >
                          <section>
                            <a
                              target="_blank"
                              title="@wildfoerster13"
                              href={`https://www.tiktok.com/@wildfoerster13/video/${id}`}
                              rel="noreferrer"
                            >
                              @wildfoerster13
                            </a>
                          </section>
                        </blockquote>
                      ) : (
                        <div 
                          className="w-full h-[575px] rounded-2xl bg-zinc-900 flex items-center justify-center border border-white/5 relative overflow-hidden group"
                        >
                           <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors z-10" />
                           <img 
                              src={`https://www.tiktok.com/api/img/?itemId=${id}&location=0`} 
                              className="absolute inset-0 w-full h-full object-cover opacity-50"
                              alt="TikTok Preview"
                           />
                           <div className="relative z-20 w-16 h-16 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/20">
                              <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[12px] border-l-white border-b-[8px] border-b-transparent ml-1" />
                           </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="mt-24 text-center">
            <a 
              href="https://www.tiktok.com/@wildfoerster13" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-4 px-10 py-5 bg-white/5 border border-white/10 rounded-full text-white text-[11px] uppercase font-black tracking-widest hover:bg-[#cfc297] hover:text-black transition-all duration-700 group shadow-2xl"
            >
              Mehr auf TikTok folgen
              <span className="group-hover:translate-x-2 transition-transform duration-500">→</span>
            </a>
        </div>
      </div>
    </section>
  );
};

export default TikTokShowcase;
